import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Edit,
  Delete,
  ExpandMore,
  Language,
  LocationOn,
  CalendarToday,
  Category,
} from '@mui/icons-material';
import { confirmAlert } from 'react-confirm-alert';
import { toast } from 'react-toastify';
import 'react-confirm-alert/src/react-confirm-alert.css';

import { useProducer } from '../context/ProducerContext';
import { IconButton } from '@mui/material';

type CardProps = {
  id: number;
  name: string;
  description?: string;
  image?: string;
  foundedYear?: number;
  headquarters?: string;
  website?: string;
  speciality?: string;
  products?: any[];
};

export default function ProducerCard(props: CardProps) {
  const [expanded, setExpanded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const { deleteProducer } = useProducer();

  const productCount = props.products?.length || 0;

  const handleDeleteProducer = async () => {
    const toastId = toast.loading('Usuwanie...', {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: 0,
      theme: 'colored',
    });

    try {
      await deleteProducer(props.id);
      toast.update(toastId, {
        render: 'Producent usunięty!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error: Error | any) {
      console.error('Failed to delete producer', error);
      toast.update(toastId, {
        render: 'Nie udało się usunąć producenta',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const handleDeleteClick = async () => {
    confirmAlert({
      title: 'Potwierdź usunięcie',
      message: 'Czy na pewno chcesz usunąć producenta?',
      buttons: [
        {
          label: 'Tak',
          onClick: () => handleDeleteProducer(),
        },
        {
          label: 'Nie',
        },
      ],
    });
  };

  return (
    <motion.div
      className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
      whileHover={{ y: -2 }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="p-4">
        <div className="flex flex-col sm:flex-row">
          {/* Logo/Obraz producenta */}
          <div className="flex-shrink-0 mb-4 sm:mb-0 sm:mr-4">
            <div className="relative overflow-hidden rounded-lg h-32 w-full sm:w-32 bg-gray-100 flex items-center justify-center">
              <motion.img
                src={
                  props.image || 'https://placehold.co/300x300?text=Brak+logo'
                }
                alt={props.name}
                className="object-contain h-full w-full"
                initial={{ opacity: 0.8 }}
                animate={{ opacity: 1 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              />

              {productCount > 0 && (
                <motion.div
                  className="absolute bottom-0 right-0 bg-primary text-white text-xs font-bold px-2 py-1 rounded-tl-lg"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  {productCount} {productCount === 1 ? 'produkt' : 'produkty'}
                </motion.div>
              )}
            </div>
          </div>

          <div className="flex-grow">
            <div className="flex justify-between items-start">
              {/* Nazwa producenta */}
              <h2 className="text-lg font-medium text-gray-900 mb-2">
                {props.name}
              </h2>

              {/* Przyciski akcji */}
              <div className="flex space-x-1">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <IconButton
                    aria-label="edit"
                    component={Link}
                    to={`edytuj/${props.id}`}
                    size="small"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <IconButton
                    aria-label="delete"
                    onClick={handleDeleteClick}
                    size="small"
                    className="text-red-500 hover:text-red-700"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </motion.div>
              </div>
            </div>

            {/* Szybkie informacje */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
              {props.foundedYear && (
                <div className="flex items-center text-sm text-gray-600">
                  <CalendarToday
                    fontSize="small"
                    className="mr-1 text-gray-400"
                  />
                  <span>Założony: {props.foundedYear}</span>
                </div>
              )}

              {props.headquarters && (
                <div className="flex items-center text-sm text-gray-600">
                  <LocationOn fontSize="small" className="mr-1 text-gray-400" />
                  <span>{props.headquarters}</span>
                </div>
              )}

              {props.speciality && (
                <div className="flex items-center text-sm text-gray-600">
                  <Category fontSize="small" className="mr-1 text-gray-400" />
                  <span>{props.speciality}</span>
                </div>
              )}

              {props.website && (
                <div className="flex items-center text-sm">
                  <Language fontSize="small" className="mr-1 text-gray-400" />
                  <a
                    href={
                      props.website.startsWith('http')
                        ? props.website
                        : `https://${props.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Strona WWW
                  </a>
                </div>
              )}
            </div>

            {/* Opis skrócony */}
            {props.description && (
              <div className="text-sm text-gray-500 line-clamp-3 mb-2">
                {props.description}
              </div>
            )}
          </div>
        </div>

        {/* Przycisk rozwijania opisu */}
        {props.description && props.description.length > 180 && (
          <div className="border-t border-gray-100 pt-2 mt-2">
            <motion.button
              className="flex items-center justify-center w-full text-sm text-gray-500 hover:text-gray-700"
              onClick={() => setExpanded(!expanded)}
              whileHover={{ backgroundColor: 'rgba(0,0,0,0.03)' }}
            >
              <span>{expanded ? 'Zwiń opis' : 'Rozwiń opis'}</span>
              <ExpandMore
                className="ml-1 transition-transform"
                style={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </motion.button>

            {expanded && (
              <motion.div
                className="mt-3 text-gray-600 text-sm p-3 bg-gray-50 rounded-md"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3 }}
              >
                {props.description}
              </motion.div>
            )}
          </div>
        )}

        {/* Link do produktów */}
        <div className="border-t border-gray-100 pt-3 mt-3">
            <Link
            to={`/szukaj?page=1&producers=%5B${props.id}%5D`}
            className="flex items-center justify-center text-primary text-sm font-medium hover:text-primary-dark transition-colors"
            >
            Zobacz wszystkie produkty
            <motion.span
              className="ml-1"
              animate={{ x: isHovering ? 3 : 0 }}
              transition={{ type: 'tween' }}
            >
              →
            </motion.span>
            </Link>
        </div>
      </div>
    </motion.div>
  );
}
